import { AUTH_BEARER_STORAGE_KEY, USER_SETTING_KEY_PREFIX, readStoredJson } from './stored-json';

/**
 * Review 7 SHARED-URL (2026-09-27): the owner-bound path to the edge, for the editors' rebuild.
 *
 * The edge no longer answers a workflow's `rehydrate` block, an input's probe or its renditions to
 * whoever knows a uuid or a stored name: every read is (signed per-file token) or (the caller's
 * bearer names the owner). This browser's bearer (`USER_SETTING_PREFIX-AUTH`, what `AuthService`
 * hands `LightHttp` for every API call) is read here without the DI graph, so the picker and the
 * Studio rebuild modules stay free of the service layer, as they are today.
 *
 * `mintInputToken` is the one new call: when the browser's own editor record is gone, the
 * workflow record names the input but carries no upload token any more; the OWNER asks the edge
 * for a fresh one (`POST /v2/edge/files/input/token`, 403 for anyone else) and everything
 * downstream -- renditions, the probe, a re-export -- runs exactly as it does after a fresh upload.
 */

/**
 * The ONE allow-list of hosts a bearer or an upload token may be sent to (review 7 SHARED-URL C-2): our edges
 * (`xconvert.com`, `*.xconvert.com`) and `localhost` for the dev loop. `v` may be a host, a host with a port, or a URL;
 * the answer is the origin-ish value as given without a trailing slash (a host stays a host, `https://…` stays so),
 * or null for anything else -- including a URL with a path, which no edge address carries. A link's `pdom`/`rdom`, a
 * reply's `uploadDomain`, a measured edge: all pass through here before a credential is attached; a foreign host makes
 * NO request at all.
 */
export function ourEdgeHost(v: unknown): string | null {
    if (typeof v !== 'string' || v.length > 253 + 8) return null;
    const host = v.replace(/^https:\/\//i, '').replace(/\/+$/, '');
    if (!/^[A-Za-z0-9.-]+(:\d{1,5})?$/.test(host)) return null;
    const name = host.replace(/:\d+$/, '').toLowerCase();
    return name === 'xconvert.com' || name.endsWith('.xconvert.com') || name === 'localhost' ? v.replace(/\/+$/, '') : null;
}

/** True when `url` (absolute) points at one of our edges -- the only place a credential may go. */
export function isOurEdgeUrl(url: string): boolean {
    try {
        const u = new URL(url);
        return u.protocol === 'https:' || u.hostname === 'localhost' ? ourEdgeHost(u.host) !== null : false;
    } catch {
        return false;
    }
}

/**
 * The stored bearer, or null -- read EXACTLY as `AuthService.getToken()` reads it (`StorageService.get(USER_SETTING_PREFIX,
 * AUTH)`: the one JSON reader in `utils/stored-json.ts`). Storage may be absent, blocked or hold junk; all of that is "none".
 */
export function storedBearer(storage?: Pick<Storage, 'getItem'> | null): string | null {
    try {
        const s = storage === undefined ? (typeof localStorage !== 'undefined' ? localStorage : null) : storage;
        const v = readStoredJson<unknown>(s, USER_SETTING_KEY_PREFIX, AUTH_BEARER_STORAGE_KEY);
        return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
    } catch {
        return null;
    }
}

/**
 * `{ Authorization: 'Bearer …' }` for the stored bearer when `url` is one of OUR edges, `{}` otherwise (no bearer, or a
 * host the allow-list does not name -- the header is never built for a URL-derived host). Spread into a fetch's headers.
 */
export function bearerHeaders(url: string, storage?: Pick<Storage, 'getItem'> | null): Record<string, string> {
    if (!isOurEdgeUrl(url)) return {};
    const t = storedBearer(storage);
    return t ? { Authorization: `Bearer ${t}` } : {};
}

export type OwnerFetch = (url: string, init?: { method?: string; headers?: Record<string, string>; body?: string; mode?: 'cors'; credentials?: 'omit' }) =>
    Promise<{ ok: boolean; status: number; json(): Promise<any> }>;

/**
 * A fresh upload token for `location` from the edge at `edgeBase` (`https://host` or `https://host/v2/edge`),
 * for the owner; null when refused (403: not this browser's file), gone, or unreachable. Never throws.
 */
export async function mintInputToken(edgeBase: string, location: string, fetchFn: OwnerFetch, storage?: Pick<Storage, 'getItem'> | null): Promise<string | null> {
    try {
        const base = edgeBase.replace(/\/+$/, '');
        const url = `${/\/v2\/edge$/.test(base) ? base : `${base}/v2/edge`}/files/input/token`;
        // C-2: a token is minted only from one of our edges; a foreign base makes no request.
        if (!isOurEdgeUrl(url)) return null;
        const res = await fetchFn(url, {
            method: 'POST', mode: 'cors', credentials: 'omit',
            headers: { 'Content-Type': 'application/json', ...bearerHeaders(url, storage) },
            body: JSON.stringify({ filename: location }),
        });
        if (!res.ok) return null;
        const body = await res.json();
        const token = body && typeof body === 'object' ? (body as { token?: unknown }).token : null;
        return typeof token === 'string' && token !== '' ? token : null;
    } catch {
        return null;
    }
}
